import type { ZodType } from "zod";
import type { QueryKey } from "@tanstack/react-query";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import { inArray, getTableColumns } from "drizzle-orm";
import type { MobileDatabase } from "../../runtime/ports";

/**
 * Per-entity-ID durable cache helper. Designed for batch by-id reads where
 * the wire shape is `T[]` and each item has a stable id.
 *
 * Contract:
 *   • Empty `ids` → `[]`, no DB hit.
 *   • Read all rows by `inArray(idColumn, ids)`. Each row's `data` is parsed
 *     against `schema`. Successes go into a Map; failures are best-effort
 *     purged so the next read isn't poisoned.
 *   • All hits + no parse failures → return reconstructed in input order,
 *     skip the network entirely.
 *   • Any miss (or any parse failure) → call `fetch(ids)` with the FULL id
 *     list, schema-validate every fresh item, then write all to SQLite in
 *     a single transaction. Return the freshly fetched items.
 *   • Fetch error → rethrow (no partial fallback). The all-hits early
 *     return above already covers the "internet died but cache was warm"
 *     case.
 *   • Schema parse fails on a fresh item → rethrow (the row is not
 *     written; the cache is not corrupted).
 *   • DB write error → log and swallow; fresh data is returned anyway.
 *
 * Order preservation: the result respects `ids`. Items missing from the
 * server response are skipped (with a warn) — final length may be < ids.
 *
 * Single-user app: no userId scoping. Logout and account switches wipe the
 * entity tables. App updates keep them: the per-row schema parse above is
 * what handles app-version drift.
 *
 * Server-returns-extras: any items the server sends back that aren't in
 * `ids` get written to the cache too (cheap insurance) but are not
 * included in the returned array.
 */
export interface WithEntityCacheOpts<T> {
  ids: string[];
  /** Drizzle table (e.g. cachedSongs). */
  entityTable: SQLiteTable;
  /** The id column on the table (e.g. cachedSongs.songId). */
  idColumn: AnySQLiteColumn;
  /** The data column on the table (e.g. cachedSongs.data). */
  dataColumn: AnySQLiteColumn;
  /** Network fetch fallback. Receives the full id list. */
  fetch: (ids: string[]) => Promise<T[]>;
  /** Per-row zod schema. Cache rows + fresh responses are parsed against it. */
  schema: ZodType<T>;
  /** Extract the entity id from a parsed item. */
  getId: (item: T) => string;
  /** Optional label used in log lines. Defaults to "withEntityCache". */
  label?: string;
}

interface ColumnKeys {
  idKey: string;
  dataKey: string;
  fetchedAtKey: string;
}

export interface ReadEntityCacheSyncOpts<T> {
  ids: string[];
  entityTable: SQLiteTable;
  idColumn: AnySQLiteColumn;
  dataColumn: AnySQLiteColumn;
  schema: ZodType<T>;
  getId: (item: T) => string;
  label?: string;
}

export function createEntityCache({
  db,
  isCurrent,
  canWrite = () => isCurrent(),
  sqlite = { inArray, getTableColumns },
}: {
  db: MobileDatabase;
  isCurrent: () => boolean;
  canWrite?: (key: readonly unknown[]) => boolean;
  sqlite?: { inArray: typeof inArray; getTableColumns: typeof getTableColumns };
}) {
  const { inArray, getTableColumns } = sqlite;
  /**
   * Resolve column-object → JS-property-name mapping for a given table. The
   * helper accepts column objects (so callers don't have to repeat string
   * keys) but `db.insert(t).values({...})` keys by the JS property name, so
   * we walk the table's column map once.
   */
  function resolveColumnKeys(
    entityTable: SQLiteTable,
    idColumn: AnySQLiteColumn,
    dataColumn: AnySQLiteColumn,
  ): ColumnKeys {
    const columns = getTableColumns(entityTable) as Record<
      string,
      AnySQLiteColumn
    >;
    let idKey = "";
    let dataKey = "";
    let fetchedAtKey = "";
    for (const [key, col] of Object.entries(columns)) {
      if (col === idColumn) idKey = key;
      if (col === dataColumn) dataKey = key;
      if ((col as { name?: string }).name === "fetched_at") fetchedAtKey = key;
    }
    return { idKey, dataKey, fetchedAtKey };
  }

  async function withEntityCache<T>(
    opts: WithEntityCacheOpts<T>,
  ): Promise<T[]> {
    const label = opts.label ?? "withEntityCache";
    if (opts.ids.length === 0) return [];
    if (!isCurrent())
      return opts
        .fetch(opts.ids)
        .then((items) => items.map((item) => opts.schema.parse(item)));

    const { idKey, dataKey, fetchedAtKey } = resolveColumnKeys(
      opts.entityTable,
      opts.idColumn,
      opts.dataColumn,
    );

    // Read step. Surrounded by try/catch so a SQLite outage looks like "no
    // hits" rather than a hard failure — we still want the network fetch to
    // run and serve users.
    const cachedById = new Map<string, T>();
    const idsToPurge: string[] = [];
    try {
      const rows = (await db
        .select()
        .from(opts.entityTable)
        .where(inArray(opts.idColumn, opts.ids))
        .all()) as unknown as Array<Record<string, unknown>>;

      for (const row of rows) {
        const rowId = row[idKey] as string;
        const parsed = opts.schema.safeParse(row[dataKey]);
        if (parsed.success) {
          cachedById.set(rowId, parsed.data);
        } else {
          // Row from an older app build with an incompatible shape. Drop it
          // so subsequent reads stop returning stale data; the network fetch
          // below will refill.
          idsToPurge.push(rowId);
        }
      }
    } catch (readErr) {
      console.warn(`[${label}] cache read failed (treating as miss)`, {
        idsCount: opts.ids.length,
        error: readErr,
      });
    }

    // Best-effort purge of rows that failed schema validation.
    if (isCurrent() && idsToPurge.length > 0) {
      try {
        console.warn(
          `[${label}] purging ${idsToPurge.length} stale cache rows`,
        );
        await db
          .delete(opts.entityTable)
          .where(inArray(opts.idColumn, idsToPurge));
      } catch (purgeErr) {
        console.warn(`[${label}] purge of stale rows failed`, {
          error: purgeErr,
        });
      }
    }

    // Fast path: every requested id is in cache and validates. No network.
    const allHit =
      idsToPurge.length === 0 && opts.ids.every((id) => cachedById.has(id));
    if (allHit) {
      const result: T[] = [];
      for (const id of opts.ids) {
        const item = cachedById.get(id);
        if (item) result.push(item);
      }
      return result;
    }

    // Slow path: at least one miss (or one parse failure). Fetch ALL ids in
    // one go — partial-chunk caching is too easy to get wrong, and the
    // upstream `fetch` callback is already chunked-and-batched in callers
    // like `fetchSongsByIds`.
    const fresh = await opts.fetch(opts.ids);

    // Validate every item before persisting. A bad shape from the server is
    // treated as a hard failure (rather than partially writing valid items
    // and partially leaving stale ones), so callers can handle it cleanly.
    const validated: T[] = fresh.map((item) => opts.schema.parse(item));

    // Single transaction write — all-or-nothing. If the server returned
    // extras that weren't requested, write those too: future reads of those
    // ids will benefit.
    if (validated.length > 0 && canWrite(opts.ids)) {
      const now = new Date();
      try {
        await db.transaction((tx) => {
          for (const item of validated) {
            const id = opts.getId(item);
            const valuesRow: Record<string, unknown> = {
              [idKey]: id,
              [dataKey]: item,
              [fetchedAtKey]: now,
            };
            const setRow: Record<string, unknown> = {
              [dataKey]: item,
              [fetchedAtKey]: now,
            };
            tx.insert(opts.entityTable)
              .values(valuesRow as never)
              .onConflictDoUpdate({
                target: opts.idColumn,
                set: setRow as never,
              })
              .run();
          }
        });
      } catch (writeErr) {
        console.warn(`[${label}] cache write-through failed`, {
          idsCount: opts.ids.length,
          error: writeErr,
        });
      }
    }

    // Reconstruct response in input order. If the server omitted any
    // requested id, warn but don't throw — the catalog routinely returns
    // fewer items than requested when an id has been hard-deleted upstream.
    const freshById = new Map<string, T>();
    for (const item of validated) freshById.set(opts.getId(item), item);

    const out: T[] = [];
    for (const id of opts.ids) {
      const item = freshById.get(id);
      if (item) {
        out.push(item);
      } else {
        console.warn(`[${label}] requested id missing from server response`, {
          id,
        });
      }
    }
    return out;
  }

  /**
   * Synchronous companion for `useQuery({ initialData })`. Returns the
   * cached items in input order, or `undefined` if any id is missing or any
   * row fails schema validation.
   *
   * The all-or-nothing return is intentional: handing react-query a partial
   * array would be misleading (it'd treat that as the answer until the
   * refetch completes). Returning undefined keeps the loading/empty branch
   * paths correct and lets the async `withEntityCache` path do the merge.
   *
   * Throws are swallowed and treated as "no data" — must never blow up the
   * render path.
   */

  function readEntityCacheSync<T>(
    opts: ReadEntityCacheSyncOpts<T>,
  ): T[] | undefined {
    return readEntityCacheRowSync<T>(opts)?.data;
  }

  /**
   * Like `readEntityCacheSync` but also exposes the OLDEST `fetchedAt`
   * timestamp across all hit rows. Used by `durableEntityQuery` to set
   * `initialDataUpdatedAt`. We use the oldest-row timestamp so a batch is
   * considered stale (and triggers a refetch) when ANY of its rows fall
   * outside the staleTime window — otherwise we'd serve a partially-stale
   * batch and never refresh.
   */
  function readEntityCacheRowSync<T>(
    opts: ReadEntityCacheSyncOpts<T>,
  ): { data: T[]; fetchedAt: Date } | undefined {
    if (!isCurrent()) return undefined;
    const label = opts.label ?? "readEntityCacheSync";
    if (opts.ids.length === 0) return { data: [], fetchedAt: new Date() };

    try {
      const { idKey, dataKey, fetchedAtKey } = resolveColumnKeys(
        opts.entityTable,
        opts.idColumn,
        opts.dataColumn,
      );

      const rows = db
        .select()
        .from(opts.entityTable)
        .where(inArray(opts.idColumn, opts.ids))
        .all() as unknown as Array<Record<string, unknown>>;

      const cached = new Map<string, T>();
      const fetchedAts: Date[] = [];
      const idsToPurge: string[] = [];
      for (const row of rows) {
        const rowId = row[idKey] as string;
        const parsed = opts.schema.safeParse(row[dataKey]);
        if (parsed.success) {
          cached.set(rowId, parsed.data);
          fetchedAts.push(row[fetchedAtKey] as Date);
        } else {
          idsToPurge.push(rowId);
        }
      }

      if (idsToPurge.length > 0) {
        // Best-effort fire-and-forget purge so future reads don't keep
        // returning stale data. Render path doesn't await disk.
        try {
          Promise.resolve(
            db
              .delete(opts.entityTable)
              .where(inArray(opts.idColumn, idsToPurge)),
          ).catch(() => {});
        } catch {
          // ignore — we still report undefined below
        }
        console.warn(
          `[${label}] purging ${idsToPurge.length} stale cache rows`,
        );
        return undefined;
      }

      // Any missing id → undefined (don't return a partial — would mislead
      // react-query into thinking it has the answer).
      for (const id of opts.ids) {
        if (!cached.has(id)) return undefined;
      }

      const out: T[] = [];
      for (const id of opts.ids) {
        const item = cached.get(id);
        if (item) out.push(item);
      }
      // Oldest fetchedAt → batch staleness governed by stalest member.
      const oldest = fetchedAts.reduce(
        (min, d) => (d < min ? d : min),
        fetchedAts[0] ?? new Date(),
      );
      return { data: out, fetchedAt: oldest };
    } catch {
      return undefined;
    }
  }

  /**
   * Transform standard `useQuery` options into a durable-entity-cached one.
   * Same shape as `durableQueryOptions` but for per-entity batch reads
   * (queries that fetch `T[]` by id and benefit from per-row caching across
   * different batch compositions). Caller passes `WithEntityCacheOpts` (ids,
   * fetch, table, columns, schema, getId) plus a `queryKey` and any standard
   * `useQuery` options; helper returns `UseQueryOptions<T[]>`.
   *
   * Auth gating is the caller's responsibility — put any check inside `fetch`.
   */
  function durableEntityQueryOptions<T, TQueryKey extends QueryKey = QueryKey>(
    opts: WithEntityCacheOpts<T> & {
      queryKey: TQueryKey;
      staleTime?: number;
      gcTime?: number;
      refetchOnMount?: boolean | "always";
      refetchOnWindowFocus?: boolean | "always";
      refetchOnReconnect?: boolean | "always";
      meta?: Record<string, unknown>;
      subscribed?: boolean;
    },
  ) {
    const {
      ids,
      entityTable,
      idColumn,
      dataColumn,
      fetch,
      schema,
      getId,
      label,
      queryKey,
      ...rest
    } = opts;
    const readOpts: ReadEntityCacheSyncOpts<T> = {
      ids,
      entityTable,
      idColumn,
      dataColumn,
      schema,
      getId,
      label,
    };
    return {
      networkMode: "always" as const,
      ...rest,
      queryKey,
      queryFn: () =>
        withEntityCache<T>({
          ids,
          entityTable,
          idColumn,
          dataColumn,
          fetch,
          schema,
          getId,
          label,
        }),
      initialData: () => readEntityCacheRowSync<T>(readOpts)?.data,
      initialDataUpdatedAt: () =>
        readEntityCacheRowSync<T>(readOpts)?.fetchedAt?.getTime() ?? 0,
    };
  }

  return {
    withEntityCache,
    readEntityCacheSync,
    readEntityCacheRowSync,
    durableEntityQueryOptions,
  };
}
